import type { ManagerGetCampaignModel, ContractorGetCampaignModel } from 'entities/campaign';
import { SchedulingDayChangeable } from 'entities/campaign';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { getKeys } from 'shared/lib/object';
import type { Timezone } from 'shared/lib/timezones';
import { timezoneOptions } from 'shared/lib/timezones';
import { AlertBox } from 'shared/ui/AlertBox';
import { Button } from 'shared/ui/Button';
import { useModalsActions } from 'shared/ui/SideModal';

import { useGetScheduleTimeRange } from '../lib/useGetScheduleTimeRange';

import { ScheduleModal } from './ScheduleModal';
import { PendingScheduleTooltip } from './PendingScheduleTooltip';

interface CallCenterBannerProps {
  campaign: ManagerGetCampaignModel | ContractorGetCampaignModel;
  appliesAt: string | null;
  isShowClock: boolean;
  timeZone: Timezone;
}

export function CallCenterBanner({ campaign, timeZone, appliesAt, isShowClock }: CallCenterBannerProps) {
  const schedule = campaign.schedule;

  const { open } = useModalsActions();

  const timeZoneValue = timezoneOptions.find((x) => x.id === timeZone)?.text;

  const { getTimeOptions, isLoading } = useGetScheduleTimeRange({ timeZone });

  return (
    <div className="flex flex-col">
      <div className="mb-4 mt-8 flex items-center justify-between">
        <div className="flex">
          <h3 className="text-lg font-semibold">Call center hours</h3>
          <PendingScheduleTooltip
            appliesAt={appliesAt}
            isShowClock={isShowClock}
          />
        </div>
        <Button
          disabled={!schedule || isLoading}
          onClick={() => {
            schedule &&
              open(
                <ScheduleModal
                  campaign={campaign}
                  appliesAt={appliesAt}
                  isShowClock={isShowClock}
                >
                  <AlertBox
                    className="my-4 border-deep-blue-100"
                    iconClass="text-deep-blue-500"
                    bodyClass="w-full"
                    iconName="info"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-normal text-slate-700">
                        These are HomeBuddy call center hours, adjusted to contractors' time zones for extra lead
                        generation.
                      </div>
                    </div>
                  </AlertBox>
                  <div className="mb-2 text-xs text-slate-700">{timeZoneValue}</div>
                  {!isLoading &&
                    getKeys(schedule).map((day, index) => (
                      <SchedulingDayChangeable
                        index={index}
                        timeOptions={getTimeOptions(day)}
                        key={day}
                        name={day}
                        period={schedule[day]}
                        schedule={schedule}
                        className="mt-2"
                        allowCopySchedule={false}
                      />
                    ))}
                </ScheduleModal>
              );
          }}
          variant="outline"
          type="button"
          size="medium"
          {...getDataAutoTestAttributes(['button-call-center-manage'])}
        >
          Manage
        </Button>
      </div>
      <AlertBox
        className="border-deep-blue-100"
        iconClass="text-deep-blue-500"
        iconName="info"
      >
        <div className="mb-2 text-sm font-medium">Call center hours has not been added</div>
        <div className="font-normal">To add HomeBuddy call center hours, click on 'Manage'.</div>
      </AlertBox>
    </div>
  );
}
